import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { getCountryCallingCode, isValidNumber, CountryCode } from "libphonenumber-js";
import { useAppDispatch } from "src/redux/storeHooks";
import { getCountriesAsync, getStateAsync, getCityAsync, createWarehouseDataAsync, updateWarehouseDetailsAsync, editInventoryAdjustmentAsync, getEditWarehouseListAsync } from "src/redux/actions/omsPageActions";
import { toast } from "react-toastify";

interface Props {
    show: boolean;
    onClose: () => void;
    editData?: any;
    onSaved?: () => void;
}

const CreateEditWarehouseModal: React.FC<Props> = ({ show, onClose, editData, onSaved }) => {
    const dispatch = useAppDispatch();
    const [formData, setFormData] = useState({
        warehouse_name: "",
        first_name: "",
        last_name: "",
        address_1: "",
        address_2: "",
        country: "",
        countryId: 0,
        state: "",
        city: "",
        zip_code: "",
        phone: "",
        fax: "",
        status: false,
        is_default: false,
    });

    const [countryList, setCountryList] = useState<{ value: number; label: string; code: string }[]>([]);
    const [stateList, setStateList] = useState<{ value: number; label: string }[]>([]);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const selectedCountry = countryList.find(c => c.label === formData.country);
    const countryDialCode = selectedCountry ? getCountryCallingCode(selectedCountry.code as CountryCode) : "";
    const maxPhoneLength = 15;
    const [isSaving, setIsSaving] = useState(false);

    // Load countries on mount
    useEffect(() => {
        dispatch(getCountriesAsync({})).then((res: any) => {
            const list = res?.payload?.map((c: any) => ({
                value: c.id,
                label: c.name,
                code: c.code,
            })) || [];
            setCountryList(list);
        });
    }, []);

    // Prefill form in edit mode
    // Prefill form in edit mode (requires countryList loaded)
    useEffect(() => {
        if (!editData || countryList.length === 0) return;
        const address = editData.address || {};
        const matchedCountry = countryList.find( (c) => c.label === address.country );
        setFormData(prev => ({
            ...prev,
            warehouse_name: editData.api_name || "",
            address_1: address.address_1 || "",
            address_2: address.address_2 || "",
            country: matchedCountry?.label || "",
            countryId: matchedCountry?.value || 0,
            state: address.state || "",
            city: address.city || "",
            zip_code: address.zip || "",
            fax: address.fax_number || "",
            phone: address.phone || "",
            status: editData?.isActive ?? false,
            is_default: editData?.is_default === true,
        }));
    }, [editData, countryList]);

    // Reset form only when modal is opened in create mode
    useEffect(() => {
        if (!show || editData) return;
        setFormData({
            warehouse_name: "",
            first_name: "",
            last_name: "",
            address_1: "",
            address_2: "",
            country: "",
            countryId: 0,
            state: "",
            city: "",
            zip_code: "",
            phone: "",
            fax: "",
            status: false,
            is_default: false,
        });
        setErrors({});
    }, [show, editData]);

    // Load states when country changes
    useEffect(() => {
        if (!formData.countryId) return;

        dispatch(getStateAsync({ country_id: formData.countryId })).then((res: any) => {
            const list = res?.payload?.map((s: any) => ({
                value: s.id,
                label: s.name,
            })) || [];
            setStateList(list);
        });
    }, [formData.countryId]);

    const handleInputChange = (e: any) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setErrors(prev => ({ ...prev, [e.target.name]: "" }));
    };

    const handlePhoneChange = (value: string) => {
        if (!/^\d*$/.test(value)) return;
        if (value.length > maxPhoneLength) return;
        setFormData({ ...formData, phone: value });
        setErrors(prev => ({ ...prev, phone: "" }));
    };

    const validate = () => {
        const errs: { [key: string]: string } = {};
        if (!formData.warehouse_name) errs.warehouse_name = "Warehouse Name is required";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setIsSaving(true);
        const payload: any = {
            api_name: formData.warehouse_name,
            fields_data: [],
            is_default: formData.is_default,
            address: {
                address_1: formData?.address_1,
                address_2: formData?.address_2,
                city: formData.city || "",
                state: stateList.find(s => s.label === formData.state)?.label || "",
                zip_code: formData?.zip_code,
                country: selectedCountry?.label || "",
                phone: formData.phone,
                fax_number: formData?.fax
            }
        };
        if (editData) {
            payload.isActive = formData.status;
        }

        try {
            if (editData) {
                await dispatch(updateWarehouseDetailsAsync({ id: editData.id, payloadupdate: payload })).unwrap()
                    .then((res: any) => {
                        if (res) {
                            toast.success(res?.message || "Warehouse updated successfully")
                        }
                    })
            } else {
                await dispatch(createWarehouseDataAsync(payload)).unwrap()
                    .then((res: any) => {
                        if (res) {
                            toast.success(res?.message || "Warehouse save successfully")
                        }
                    })
            }
            if (onSaved) onSaved();
        } catch (error: any) {
            console.error("Save failed:", error);
            toast.error(error?.message || "Failed to save warehouse");
        }finally {
        setIsSaving(false);
    }
    };

    return (
        <Modal show={show} onHide={onClose} centered size="lg" backdrop="static">
            <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ background: "#f8f9fa", borderBottom: "1px solid #e1e1e1" }}>
                <h5 className="m-0 fw-bold">{editData ? "Edit Warehouse" : "Create Warehouse"}</h5>
                <div className="d-flex gap-2">
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
<button className="btn btn-primary" onClick={handleSubmit} disabled={isSaving}>
    {isSaving ? "Saving..." : "Save"}
</button>
                </div>
            </div>

            <Modal.Body className="px-4 pb-4">
                <div className="row gy-3">
                    {/* Warehouse Name */}
                    <div className="col-12">
                        <label className="form-label fw-semibold required">Warehouse Name</label>
                        <input
                            name="warehouse_name"
                            className={`form-control ${errors.warehouse_name ? "is-invalid" : ""}`}
                            value={formData.warehouse_name}
                            onChange={handleInputChange}
                        />
                        {errors.warehouse_name && <div className="invalid-feedback">{errors.warehouse_name}</div>}
                    </div>

                    {/* Address 1 */}
                    <div className="col-md-6">
                        <label className="form-label fw-semibold ">Address Line 1</label>
                        <input
                            name="address_1"
                            className={`form-control ${errors.address_1 ? "is-invalid" : ""}`}
                            value={formData.address_1}
                            onChange={handleInputChange}
                        />
                        {errors.address_1 && <div className="invalid-feedback">{errors.address_1}</div>}
                    </div>
                    {/* Address 2 */}
                    <div className="col-md-6">
                        <label className="form-label fw-semibold">Address Line 2</label>
                        <input
                            name="address_2"
                            className="form-control"
                            value={formData.address_2}
                            onChange={handleInputChange}
                        />
                    </div>
                    {/* Country */}
                    <div className="col-md-4">
                        <label className="form-label fw-semibold ">Country</label>
                        <Select
                            options={countryList}
                            value={countryList.find(c => c.label === formData.country) || null}
                            onChange={opt => {
                                setFormData({ ...formData, country: opt!.label, countryId: opt!.value, state: "", });
                                setErrors(prev => ({ ...prev, country: "" }));
                            }}
                        />
                        {errors.country && <div className="text-danger mt-1">{errors.country}</div>}
                    </div>
                    {/* State */}
                    <div className="col-md-4">
                        <label className="form-label fw-semibold ">State</label>
                        <Select
                            options={stateList}
                            value={stateList.find(s => s.label.toLowerCase() === formData.state.toLowerCase()) || null}
                            onChange={opt => {
                                setFormData({ ...formData, state: opt!.label, });
                                setErrors(prev => ({ ...prev, state: "" }));
                            }}
                        />
                        {errors.state && <div className="text-danger mt-1">{errors.state}</div>}
                    </div>
                    {/* City (text input instead of dropdown) */}
                    <div className="col-md-4">
                        <label className="form-label fw-semibold">City</label>
                        <input
                            name="city"
                            className={`form-control ${errors.city ? "is-invalid" : ""}`}
                            value={formData.city}
                            onChange={handleInputChange}
                            placeholder="Enter city"
                            style={{ height: "2.9rem" }}
                        />
                        {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                    </div>
                    {/* Zip Code */}
                    <div className="col-md-4">
                        <label className="form-label fw-semibold ">Zip Code</label>
                        <input
                            name="zip_code"
                            className={`form-control ${errors.zip_code ? "is-invalid" : ""}`}
                            value={formData.zip_code}
                            onChange={handleInputChange}
                        />
                        {errors.zip_code && <div className="invalid-feedback">{errors.zip_code}</div>}
                    </div>
                    {/* Phone */}
                    <div className="col-md-4">
                        <label className="form-label fw-semibold ">Phone</label>
                        <div className="input-group">
                            <span className="input-group-text">+{countryDialCode}</span>
                            <input
                                className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                                value={formData.phone}
                                onChange={e => handlePhoneChange(e.target.value)}
                                maxLength={maxPhoneLength}
                                placeholder="Phone number"
                            />
                            {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                        </div>
                    </div>
                    {/* Fax */}
                    <div className="col-md-4">
                        <label className="form-label fw-semibold">Fax</label>
                        <input
                            name="fax"
                            className="form-control"
                            value={formData.fax}
                            onChange={handleInputChange}
                            placeholder="Fax number"
                        />
                    </div>
                    <div className="col-12">
                        <div className="d-flex align-items-center">
                            <label
                                className="d-inline-flex align-items-center gap-2 mb-0"
                                style={{ cursor: "pointer" }}
                            >
                                <input
                                    type="checkbox"
                                    checked={formData.is_default}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            is_default: e.target.checked,
                                        })
                                    }
                                    style={{
                                        width: "16px",
                                        height: "16px",
                                        accentColor: "#0d6efd",
                                        cursor: "pointer",
                                    }}
                                />
                                <span className="fw-semibold text-dark">
                                    Set as default warehouse
                                </span>
                            </label>
                        </div>
                    </div>
                    {editData && (
                        <div className="col-md-4">
                            <label className="form-label fw-semibold">Status</label>
                            <div className="d-flex gap-3 align-items-center">

                                <label className="d-flex align-items-center gap-2">
                                    <input
                                        type="radio"
                                        name="status"
                                        checked={formData.status === true}
                                        onChange={() => setFormData({ ...formData, status: true })}
                                    /> Active
                                </label>

                                <label className="d-flex align-items-center gap-2">
                                    <input
                                        type="radio"
                                        name="status"
                                        checked={formData.status === false}
                                        onChange={() => setFormData({ ...formData, status: false })}
                                    /> Inactive
                                </label>

                            </div>
                        </div>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default CreateEditWarehouseModal;
